import { useState, useEffect } from 'react';
import { Button, Table } from 'semantic-ui-react';
import { CSVLink } from "react-csv";
import { useSessionStorage } from '../helpers'
import { trackEvent } from '@eeacms/volto-matomo/utils';
import { AcousticFrequencies, AcousticHighlight } from './acoustic';
import { GeneratePdfSubmittal } from './submittal';

export function SelectionStorage() {
    return useSessionStorage('selection', {items:{'damper': [],
                                                  'diffuser':[],
                                                  'louver':[],
                                                  'silencer':[]},
                                           project:{'project_name': '',
                                                    'reference': ''}});
}


function Highlight({name, value, acoustic, pressure_drop_min}){
    const frequencies = AcousticFrequencies({acoustic: undefined });
    const freq = name.replace(/insertion_loss_/g, '');
    if ( frequencies.includes(freq)){
        return ( <AcousticHighlight value={value} 
                                    best={ acoustic?.acoustic_max?.[freq]} 
                                    performance={acoustic?.acoustic_insertion?.[freq] || 0.0} />)
    } else {
        if (name === 'pressure_drop' && pressure_drop_min && pressure_drop_min >= value.replace(/ Pa/, '')) {
            return (<strong>{value}</strong>);
        } else {
         return value;
        }
    }
}

export function SelectionTable({selection, setSelection}) {
    const frequencies = AcousticFrequencies({acoustic: undefined });
    const headers = {'louver':[
        {'label': 'Reference', key: 'reference', input: true},
        {'label': 'Model', key: 'model'},
        {'label': 'Nominal Size', key: 'nominal_size'},
        {'label': 'Section Size', key: 'sections'},
        {'label': 'Flow Rate', key: 'flow_rate'},
        {'label': 'Free Area Velocity', key: 'free_area_velocity'},
        {'label': 'Pressure Drop (intake)', key: 'pressure_drop_intake'},
        {'label': 'Pressure Drop (exhaust)', key: 'pressure_drop_exhaust'},
        {'label': 'Free Area', key: 'free_area'},
        {'label': 'Estimated Weight', key: 'estimated_weight'},
        {'label': 'Efficiency', key: 'efficiency'},
        {'label': 'Wind Loading', key: 'wind_loading'},
        {'label': 'Amount', key: 'amount', input: true},
        {'label': 'UUID', key: 'uuid', input: true},
        ],
        'damper': [
        {'label': 'Reference', key: 'reference', input: true},
        {'label': 'Model', key: 'model'},
        {'label': 'Nominal Size', key: 'nominal_size'},
        {'label': 'Section Size', key: 'sections'},
        {'label': 'Penetration Size', key: 'penetration_size'},
        {'label': 'Overall Size', key: 'overall_size'},
        {'label': 'Flow Rate', key: 'flow_rate'},
        {'label': 'Free Area Velocity', key: 'free_area_velocity'},
        {'label': '∆Pressure (leakage)', key: 'pressure_leakage'},
        {'label': 'Leakage (closed)', key: 'leakage'},
        {'label': 'Blade Position at ∆Pressure', key: 'blade_position'},
        {'label': 'Free Area', key: 'free_area'},
        {'label': 'Free Area (%)', key: 'free_area_pc'},
        {'label': 'Pressure Drop', key: 'pressure_drop'},
        {'label': 'Estimated Weight', key: 'estimated_weight'},
        {'label': 'Amount', key: 'amount', input: true},
        {'label': 'UUID', key: 'uuid', input: true},
        ],
        'diffuser': [
        {'label': 'Reference', key: 'reference', input: true},
        {'label': 'Model', key: 'model'},
        {'label': 'Attributes', key: 'attributes'},
        {'label': 'Nominal Size', key: 'nominal_size'},
        {'label': 'Overall Size', key: 'overall_size'},
        {'label': 'Flow Rate', key: 'flow_rate'},
        {'label': 'Flow Rate Side A', key: 'flow_rate_side_a'},
        {'label': 'Flow Rate Side B', key: 'flow_rate_side_b'},
        {'label': 'Static Pressure', key: 'static_pressure'},
        {'label': 'Horizontal Throw @ 0.25m/s', key: 'horizontal_throw_0.25'},
        {'label': 'Vertical Throw @ 0.25m/s', key: 'vertical_throw_0.25'},
        {'label': 'Side A Throw @ 0.25m/s', key: 'side_a_throw_0.25'},
        {'label': 'Side B Throw @ 0.25m/s', key: 'side_b_throw_0.25'},
        {'label': 'Throw @ 0.25m/s', key: 'throw_0.25'},
        {'label': 'Spread @ 0.25m/s', key: 'spread_0.25'},
        {'label': 'NC', key: 'nc'},
        {'label': 'Amount', key: 'amount', input: true},
        {'label': 'UUID', key: 'uuid', input: true},
        ],
        'silencer': [
        {'label': 'Reference', key: 'reference', input: true},
        {'label': 'Model', key: 'model'},
        {'label': 'Nominal Size', key: 'nominal_size', header: 'Silencer Information'},
        {'label': 'Overall Size', key: 'overall_size'},
        {'label': 'Length', key: 'length'},
        {'label': 'Component Size', key: 'component_size'},
        {'label': 'Components', key: 'number_of_components'},
        {'label': 'Module Width', key: 'module_width'},
        {'label': 'Modules', key: 'number_of_modules'},
        {'label': 'Inlet Leg', key: 'inlet_leg'},
        {'label': 'Outlet Leg', key: 'outlet_leg'},
        {'label': 'Weight', key: 'weight'},
        {'label': 'Airflow', key: 'flow_rate', header: 'Duct System Information'},
        {'label': 'Airflow Direction', key: 'direction'},
        {'label': 'Pressure Drop', key: 'pressure_drop'},
        {'label': 'Installed Pressure Drop', key: 'installed_pressure_drop'},
        ...frequencies.map((freq, index) => ({label: freq,
                                             key: 'insertion_loss_' + freq,
                                             header: index === 0 ? 'Insertion Loss (dB)': ''
                                             })),
        ...frequencies.map((freq, index) => ({label: freq,
                                    key: 'generated_noise_' + freq,
                                    input: true,
                                    header: index === 0 ? 'Generated Noise (dB)': ''
        })),
        {'label': 'Acoustic Performance', key: 'acoustic', input: true},
        {'label': 'Notes', key: 'notes', input: true},
        {'label': 'Amount', key: 'amount', input: true},
        {'label': 'UUID', key: 'uuid', input: true},
        ]
    };
    const [showTable, setShowTable] = useState(false);
    // Hide entirely if no entries
    /* if ( Object.values(selection.items).reduce((a, e)  => a + e.length, 0) == 0){
        return null;
    }*/
    useEffect(() => {
        if ( Object.values(selection.items).reduce((a, e)  => a + e.length, 0) == 1){
            setShowTable(true);
        }
    }, [selection]);

    const handleClear = (event, key) => {
        trackEvent({category: 'selection', action: "clear", name: key});
        setSelection({...selection,
                     items: {...selection.items,
                              [key]:[]}});
        if ( Object.values(selection.items).reduce((a, e)  => a + e.length, 0) == 0){
            setShowTable(false);
        }
    };
    const handleRowDel = (event, key, row) => {
        let list = [...selection.items[key]]
        trackEvent({category: 'selection', action: "delete", name: list[row]['model']});
        list.splice(row, 1);
        setSelection({...selection,
                      items: {...selection.items,
                              [key]:[...list]}});
        if ( Object.values(selection.items).reduce((a, e)  => a + e.length, 0) == 0){
            setShowTable(false);
        }
    };
    const handleChange = (event, key, row) => {
        const { name, value } = event.target;
        const list = [...selection.items[key]];
        list[row][name] = value;
        trackEvent({category: 'selection', action: "change", name: list[row]['model'], value: list[row]['amount']});
        setSelection({...selection,
                      items: {...selection.items,
                              [key]:[...list]}});
    };
    const handleProjectChange = (event) => {
        const { name, value } = event.target;
        const project = {...selection.project};
        project[name] = value;
        setSelection({...selection,
                      project: {...project}});
    };
    const searchParams = new URLSearchParams(document.location.search)
    const pathname = document.location.pathname;
    if (searchParams.get('debug')){
        console.log('table.js', selection);
    }
    
    function download ( {product, items} ){
        trackEvent({category: 'selection', action: "download", name: product, value: items.length });
    }

    function selectionButton ( value ){
        trackEvent({category: 'selection', action: (value ? "show" : "hide")});
        setShowTable(value); 
    }
    if ( showTable )  {
        return (
            <>
            <Button className="ui button add blue" onClick={() => selectionButton(false)}>Hide Selection</Button> 
            <form className="project">
                <h3 className="blue text">Project Name: <input type="text" name="project_name" value={selection?.project?.project_name} onChange={(event)=>handleProjectChange(event)} className="project" /></h3>
            </form>
                { Object.entries(selection.items).filter(([product, items]) => (items.length || pathname.includes(product))).map(([product, items]) => (
            <form className="selectionForm" key={ product }> 

                <h3 className="blue text">{ product }</h3>
                <Table className={'selection ' + product}>
                    {<Table.Header>
                            { (product === 'silencer') &&
                            <Table.Row key="header1">
                                <Table.HeaderCell key="info" className="information" colSpan="12">Silencer Information</Table.HeaderCell>
                                <Table.HeaderCell key="system" className="system" colSpan="4">Duct System Information</Table.HeaderCell>
                                <Table.HeaderCell key="noise" className="noise" colSpan="8">Insertion Loss (dB) / Generated Noise (dB)</Table.HeaderCell>
                            </Table.Row>
                            }
                            <Table.Row key="column names">
                                <Table.HeaderCell className="reference">Reference</Table.HeaderCell>
                                {headers[product].map((header) => (
                                    ( !header?.input ) && (
                                <Table.HeaderCell className={header?.key} key={ header?.label }>{ header?.label }</Table.HeaderCell>
                                )))}
                                <Table.HeaderCell className="amount" key="amount">
                                <input type="button" onClick={(event)=>handleClear(event, product)} value="X" title="clear all" className="del-btn"/>
                                 Amount</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>}
                    { <Table.Body>
                             
                        { /* this is stupidly complicated due to wierdness with missing key and <></> with two separate rows */
                            items?.map((item, row) => { 
                                let items = [item];
                                if ( product === 'silencer'){
                                    items = [item, item];
                                }
                                return items.map((item, line) => {
                                  const row_id = (item?.uuid || row) + line;
                                  return (<Table.Row key={ row_id }>
                                      {(line === 0) ?
                                      (<>
                                      <Table.Cell key="ref"><input type="text" name="reference" value={item?.reference} onChange={(event)=>handleChange(event, product, row)} className="reference" /> </Table.Cell> 
                                    {headers[product].map((header, index) => (
                                        ( !header?.input ) && (
                                      <Table.Cell key={header.key} className={item?.submittal?.warnings?.[header.key] || ''}>
                                          <Highlight name={header.key} value={item?.[header.key] || ''} acoustic={item?.acoustic} pressure_drop_min={item?.submittal?.pressure_drop_min} /> 
                                      </Table.Cell> 
                                      )))}
                                      <Table.Cell key="amount">
                                          <input type="button" onClick={(event)=>handleRowDel(event, product, row)} value="X" title="delete" className="del-btn"/>
                                          <input type="number" name="amount" value={item?.amount} onChange={(event)=>handleChange(event, product, row)} className="number" /> </Table.Cell>
                                      </>) : (
                                      <>
                                      <Table.Cell key="filler"></Table.Cell> 
                                      <Table.Cell key="notes" className="notes" colSpan="15">{item?.notes}</Table.Cell> 
                                      { frequencies.map(freq => (
                                      <Table.Cell key={'generated_noise_' + freq} >{item?.['generated_noise_' + freq]}</Table.Cell>
                                      ))}
                                      </>)}
                                  </Table.Row>)

                                });
                            })}
                    </Table.Body>}
                </Table>
                { (product==='silencer') ? 
                <>
                <CSVLink className="ui button add blue" data={ [Object.assign({}, ...headers?.silencer.map((header) => ({[header.key]: header.label}))),
                                                                ...selection?.items[product]] }
                                                        headers={ headers[product].filter((header) => (header.key != 'acoustic' && header.key != 'uuid')).map((header) => ({label:header.header, key:header.key}))
                    } filename="selection.csv" onClick={(event) => download({product, items})}>
                    Download Selection as csv</CSVLink>
                </> 
                :
                <CSVLink key={product + 'csv'} className="ui button add blue" data={items} headers={headers[product].filter((header) => header.key != 'uuid')} headerrows={0} filename="selection.csv" onClick={(event) => download({product, items})}>
                    Download Selection as csv</CSVLink> }
            </form>
            ))}
           {(selection.items['silencer'].length > 0) && <GeneratePdfSubmittal key={'submittal'} selection={ selection } setSelection={ setSelection } />}
            </>

        )
    } else {
        return (  <Button className="ui button add blue" onClick={() => selectionButton(true)}>Show Selection ({ Object.values(selection.items).reduce((a, e)  => a + e.length, 0) })</Button> )
    }
}


